import ProtectedComponent from './protected-component.js';
import logger from '../utils/logger.js';
import { teardownGroupContextNav } from '../utils/group-context-nav.js';
import { renderSocietyOtter } from '../utils/mascots.js';

/**
 * /groups — list memberships, cards with optional upcoming events.
 */
class GroupsHub extends ProtectedComponent {
  constructor() {
    super();
    this.groups = [];
    this.archivedGroups = [];
    this.pendingInvites = [];
    this.error = null;
    this.loading = true;
    this.showArchived = false;
  }

  connectedCallback() {
    super.connectedCallback();
    teardownGroupContextNav();
    this.load();
  }

  navigateToCreate() {
    if (window.router?.navigate) {
      window.router.navigate('/groups/new');
    } else {
      window.location.href = '/groups/new';
    }
  }

  async load() {
    if (!window.API || !this.currentUser) {
      return;
    }
    this.loading = true;
    this.render();
    try {
      const eventsQuery = this.showArchived ? '' : '?include_upcoming_events=1';
      const requests = [
        window.API.request(`/groups${eventsQuery}`)
      ];
      if (!this.showArchived) {
        requests.push(window.API.request('/groups/invites/mine'));
      }
      const results = await Promise.all(requests);
      this.groups = results[0].groups || [];
      this.pendingInvites = this.showArchived ? [] : (results[1]?.invites || []);
      this.error = null;
    } catch (e) {
      this.error = e.message || 'Could not load groups';
      logger.warn('GroupsHub load failed', e);
    } finally {
      this.loading = false;
      this.render();
    }
  }

  async loadArchivedGroups() {
    if (!window.API || !this.currentUser) {
      return;
    }
    try {
      const data = await window.API.request('/groups?archived_only=1');
      this.archivedGroups = data.groups || [];
    } catch (e) {
      logger.warn('GroupsHub archived load failed', e);
      this.archivedGroups = [];
    }
    this.render();
  }

  render() {
    if (!this.currentUser) {
      this.innerHTML = '';
      return;
    }

    if (this.loading) {
      this.innerHTML = `
        <div class="groups-container">
          <div class="loading">Loading groups...</div>
        </div>
      `;
      return;
    }

    const showCta = !this.showArchived && this.groups.length > 0;

    this.innerHTML = `
      <div class="groups-container">
        ${showCta ? '<add-group-cta></add-group-cta>' : ''}

        <div class="groups-header">
          <h1>My Groups</h1>
          <div class="header-actions">
            <button type="button" class="btn-create-group" id="createGroupBtn">
              🌱 Create Group
            </button>
          </div>
        </div>

        <toolbar-action-bar label="Filters">
          <div slot="actions" class="toggle-actions">
            <label class="toggle-checkbox-label icon-only" title="Show archived groups">
              <span class="toggle-icon">🗑️</span>
              <input type="checkbox" class="toggle-checkbox" id="showArchivedCheckbox" ${this.showArchived ? 'checked' : ''}>
              <span class="toggle-checkbox-custom"></span>
            </label>
          </div>
        </toolbar-action-bar>

        <div id="groupsHubError">
          ${this.error ? `<p class="error">${this.escapeHtml(this.error)}</p>` : ''}
        </div>

        ${this.renderPendingInvites()}

        ${this.renderBody()}
      </div>
    `;

    const cta = this.querySelector('add-group-cta');
    if (cta && typeof cta.setCurrentUser === 'function') {
      cta.setCurrentUser(this.currentUser);
    }

    this.attachEventListeners();
  }

  renderPendingInvites() {
    if (this.showArchived || !this.pendingInvites.length) {
      return '';
    }
    return `
      <section class="groups-hub-pending card">
        <h2 class="groups-hub-pending-title">Pending invitations</h2>
        <ul class="groups-hub-pending-list">
          ${this.pendingInvites.map((inv) => this.renderPendingInvite(inv)).join('')}
        </ul>
      </section>
    `;
  }

  renderPendingInvite(inv) {
    const inviter = inv.inviter?.username || 'Someone';
    const groupName = inv.group?.name || 'a group';
    const token = inv.invite_token;
    return `
      <li class="groups-hub-pending-item">
        <div class="groups-hub-pending-info">
          <strong>${this.escapeHtml(inviter)}</strong>
          invited you to <strong>${this.escapeHtml(groupName)}</strong>
        </div>
        <div class="groups-hub-pending-actions">
          <a class="btn btn-secondary btn-sm" href="/groups/invites/${this.escapeHtml(token)}">View</a>
          <button type="button" class="btn btn-primary btn-sm" data-invite-accept="${this.escapeHtml(token)}">Accept</button>
          <button type="button" class="btn btn-secondary btn-sm" data-invite-decline="${this.escapeHtml(token)}">Decline</button>
        </div>
      </li>
    `;
  }

  renderBody() {
    if (this.showArchived) {
      if (!this.archivedGroups.length) {
        return this.renderEmptyArchived();
      }
      return `
        <div class="groups-hub-grid">
          ${this.archivedGroups.map((g) => this.renderGroupCard(g, { archived: true })).join('')}
        </div>
      `;
    }

    if (!this.groups.length) {
      return this.renderEmpty();
    }

    return `
      <div class="groups-hub-grid">
        ${this.groups.map((g) => this.renderGroupCard(g)).join('')}
      </div>
    `;
  }

  renderEmpty() {
    return `
      <div class="empty-state">
        <div class="groups-mascot">
          ${renderSocietyOtter()}
        </div>
        <h2>Hi there! I'm Society Otter!</h2>
        <p class="groups-mascot-message">
          I'm SO excited to help you start your first group!
          Whether it's a book club, neighborhood pals, or your game night crew,
          we'll build a cozy little community together.
        </p>
        <p class="groups-mascot-encouragement">
          Trust me — good groups make Fun Old Times even more fun.
          Let's gather your people! 🌱
        </p>
        <button type="button" class="btn-create-group" id="createGroupBtnEmpty">
          ✨ Create My First Group ✨
        </button>
      </div>
    `;
  }

  renderEmptyArchived() {
    return `
      <div class="empty-state">
        <div class="groups-mascot">
          ${renderSocietyOtter()}
        </div>
        <h2>Nothing archived here! 🎉</h2>
        <p class="groups-mascot-message">
          No archived groups found. Society Otter is doing a little happy dance
          because everything is still active and swimming along nicely!
        </p>
      </div>
    `;
  }

  renderGroupCard(g, { archived = false } = {}) {
    const archivedBadge = archived
      ? '<span class="groups-hub-card-archived">🗑️ Archived</span>'
      : '';
    const eventsStrip = archived ? '' : this.renderEventsStrip(g.upcoming_events);
    return `
      <button type="button"
        class="groups-hub-card${archived ? ' groups-hub-card--archived' : ''}"
        data-groups-nav="${archived ? '' : `/groups/${g.id}`}"
        ${archived ? 'disabled' : ''}>
        <div class="groups-hub-card-head">
          <h3 class="groups-hub-card-name">${this.escapeHtml(g.name)}</h3>
          ${archivedBadge}
        </div>
        <div class="groups-hub-card-role">${this.escapeHtml(g.role)}</div>
        ${g.description ? `<p class="groups-hub-card-desc">${this.escapeHtml(g.description)}</p>` : ''}
        ${eventsStrip}
      </button>
    `;
  }

  renderEventsStrip(evts) {
    if (!evts || !evts.length) {
      return '<p class="groups-hub-events-empty">No upcoming linked events.</p>';
    }
    const items = evts.slice(0, 3).map((e) => {
      const when = e.start_time
        ? new Date(e.start_time).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })
        : '';
      const id = encodeURIComponent(String(e.id));
      return `<li><a href="/events/${id}">${when ? `${when} · ` : ''}${e.title || 'Event'}</a></li>`;
    }).join('');
    return `
      <div class="groups-hub-events">
        <h4>Upcoming</h4>
        <ul>${items}</ul>
      </div>
    `;
  }

  attachEventListeners() {
    this.querySelectorAll('#createGroupBtn, #createGroupBtnEmpty').forEach((btn) => {
      btn.addEventListener('click', () => this.navigateToCreate());
    });

    const archivedToggle = this.querySelector('#showArchivedCheckbox');
    if (archivedToggle) {
      archivedToggle.addEventListener('change', async (ev) => {
        this.showArchived = ev.target.checked;
        if (this.showArchived && !this.archivedGroups.length) {
          await this.loadArchivedGroups();
        } else {
          if (!this.showArchived) {
            await this.load();
          } else {
            this.render();
          }
        }
      });
    }

    this.querySelectorAll('[data-groups-nav]').forEach((el) => {
      const path = el.getAttribute('data-groups-nav');
      if (!path) return;
      el.addEventListener('click', (ev) => {
        ev.preventDefault();
        if (window.router?.navigate) {
          window.router.navigate(path);
        } else {
          window.location.href = path;
        }
      });
    });

    this.querySelectorAll('[data-invite-accept]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const token = btn.getAttribute('data-invite-accept');
        try {
          const result = await window.API.request(`/groups/invites/${token}/accept`, { method: 'POST' });
          if (window.router?.navigate) {
            window.router.navigate(`/groups/${result.group_id}`);
          }
        } catch (e) {
          logger.warn('Accept invite from hub failed', e);
          this.error = e.message || 'Could not accept invitation';
          this.render();
        }
      });
    });

    this.querySelectorAll('[data-invite-decline]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const token = btn.getAttribute('data-invite-decline');
        try {
          await window.API.request(`/groups/invites/${token}/decline`, { method: 'POST' });
          this.pendingInvites = this.pendingInvites.filter((i) => i.invite_token !== token);
          this.render();
        } catch (e) {
          logger.warn('Decline invite from hub failed', e);
          this.error = e.message || 'Could not decline invitation';
          this.render();
        }
      });
    });

    this.querySelectorAll('.groups-hub-pending-actions a[href^="/groups/invites/"]').forEach((link) => {
      link.addEventListener('click', (ev) => {
        ev.preventDefault();
        const path = link.getAttribute('href');
        if (window.router?.navigate) {
          window.router.navigate(path);
        }
      });
    });
  }

  setCurrentUser(user) {
    super.setCurrentUser(user);
    if (user) {
      this.load();
    } else {
      this.innerHTML = '';
    }
  }

  escapeHtml(s) {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

customElements.define('groups-hub', GroupsHub);
export default GroupsHub;
